import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { PageLayout } from './shared/enums/page-layout';
import { PageLayoutService } from './shared/services/page-layout/page-layout.service';
import { AuthorizedLayoutComponent } from './shared/components/authorized-layout/authorized-layout.component';
import { AsyncPipe } from '@angular/common';
import { UnauthorizedLayoutComponent } from './shared/components/unauthorized-layout/unauthorized-layout.component';
import { environment } from '../environments/environment';
import { CommonService } from './shared/services/common/common.service';
import { ToastActionType, ToastType } from './shared/interfaces/common';
import { takeUntil, filter } from 'rxjs';
import { DestroyBase } from './shared/base-classes/destroy-component-base';
import { AppToastComponent } from './shared/components/app-toast/app-toast.component';
import { SplashScreenComponent } from './shared/components/splash-screen/splash-screen.component';
import { SplashScreenService } from './shared/services/splash-screen/splash-screen.service';
import { AchievementUnlockModalComponent } from './shared/components/achievement-unlock-modal/achievement-unlock-modal.component';
import { ReportContentModalComponent } from './shared/components/report-content-modal/report-content-modal.component';
import { ReportContentService } from './shared/services/reports/report-content.service';
import { AccessDeniedModalComponent } from './shared/components/access-denied-modal/access-denied-modal.component';
import { MicrosoftClarityService } from './shared/services/microsoft-clarity/microsoft-clarity.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    LoaderComponent,
    AsyncPipe,
    AuthorizedLayoutComponent,
    UnauthorizedLayoutComponent,
    AppToastComponent,
    SplashScreenComponent,
    AchievementUnlockModalComponent,
    ReportContentModalComponent,
    AccessDeniedModalComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends DestroyBase implements OnInit {
  title = 'ng-acc-dh';

  pageLayoutService = inject(PageLayoutService);
  commonService = inject(CommonService);
  splashScreenService = inject(SplashScreenService);
  reportContentService = inject(ReportContentService);
  clarityService = inject(MicrosoftClarityService);
  router = inject(Router);
  readonly PageLayout = PageLayout;

  toastType: ToastActionType = ToastType.Success;
  message = '';
  showToast = false;

  isReportPopupOpen = false;

  ngOnInit(): void {
    // Prevent mouse wheel from changing number inputs globally
    document.addEventListener('wheel', (event: WheelEvent) => {
      if (
        event.target instanceof HTMLInputElement &&
        event.target.type === 'number'
      ) {
        event.target.blur();
      }
    });

    this.loadMicrosoftClarityScript();
    this.clarityService.initializeUsageTracking();
    this.commonService.toast$
      .pipe(takeUntil(this.destroy$))
      .subscribe((toast) => {
        this.toastType = toast.type;
        this.message = toast.message;
        this.showToast = true;

        setTimeout(() => {
          this.showToast = false;
        }, toast.duration || 3000);
      });

    this.reportContentService.isOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => (this.isReportPopupOpen = res));

    // Hide splash screen when navigation completes (except login-callback and dashboard)
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        if (
          !event.url.includes('/login-callback') &&
          !event.url.includes('/login') &&
          !event.url.includes('/dashboard')
        ) {
          // Add delay to ensure smooth transition after auth redirect
          setTimeout(() => {
            this.splashScreenService.hide();
          }, 1000);
        }
      });
  }

  loadMicrosoftClarityScript() {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.text = `
      (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        t = l.createElement(r);
        t.async = 1;
        t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      })(window, document, "clarity", "script", "${environment.microsoftClarityId}");
    `;
    document.head.appendChild(script);
  }
}
